import React, { useEffect, useState } from 'react';
import apiService from '../services/apiService';
import FlashMessage from './FlashMessage';
import {
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
} from '../styles/SubscriptionFormModalStyles';
import {
  BillsTableWrapper,
  BillsTableEl,
  BillsTableHeader,
  BillsTableRow,
  BillsTableData,
  StatusBadgeBills,
} from '../styles/BillsTableStyles';
import { FormRow, Label, SubmitButton } from '../styles/ComponentStyles';
import { BillNumberInput, BillDateInput } from '../styles/BillsFormStyles';
import { balanceColor, statusColor, formatMoney } from '../utils/receivableMath';
import { sortByDate } from '../utils/sortByDate';

const emptyDate = () => new Date().toISOString().slice(0, 10);

const ReceivableDetailModal = ({ receivable, onClose, onRefresh }) => {
  const [item, setItem] = useState(receivable || null);
  const [loading, setLoading] = useState(true);
  const [flashMessage, setFlashMessage] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [newPayment, setNewPayment] = useState({
    amount: '',
    date: emptyDate(),
    method: '',
    payment_type: 'Principal',
  });

  const setFlash = (msg) => {
    setFlashMessage(msg);
    setTimeout(() => setFlashMessage(''), 3000);
  };

  const fetchItem = async () => {
    if (!receivable?._id) return;
    setLoading(true);
    try {
      const res = await apiService.getReceivable(receivable._id);
      const detail = res?.data?.item || res?.item || res;
      setItem(detail);
      // Prefill the payment amount with the fixed installment when applicable.
      if (detail?.fixed_installment && detail?.installment_amount != null) {
        setNewPayment((p) => ({
          ...p,
          amount: p.amount || String(detail.installment_amount),
        }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivable?._id]);

  const payments = item?.payments || [];
  const toggleSortDir = () => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
  const sortedPayments = sortByDate(payments, 'date', sortDir);

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!newPayment.amount || !newPayment.date) {
      setFlash('Please fill amount and date.');
      return;
    }
    try {
      await apiService.recordReceivablePayment(receivable._id, {
        amount: Number(newPayment.amount),
        date: newPayment.date,
        method: newPayment.method || null,
        payment_type: newPayment.payment_type || 'Principal',
      });
      setFlash('Payment recorded.');
      setNewPayment({ amount: '', date: emptyDate(), method: '', payment_type: 'Principal' });
      await fetchItem();
      if (onRefresh) onRefresh();
    } catch (err) {
      setFlash(err?.response?.data?.detail || err?.message || 'Failed to record payment.');
    }
  };

  const handleDeletePayment = async (id) => {
    try {
      await apiService.deleteReceivablePayment(receivable._id, id);
      setFlash('Payment deleted.');
      await fetchItem();
      if (onRefresh) onRefresh();
    } catch (err) {
      setFlash(err?.response?.data?.detail || 'Failed to delete.');
    }
  };

  const deleteBtnStyle = {
    background: 'none',
    border: 'none',
    color: '#f44336',
    cursor: 'pointer',
  };

  const isFixed = item?.fixed_installment && item?.installment_amount != null;

  return (
    <ModalBackdrop onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
        <ModalHeader>
          <ModalTitle>{item?.debtor || 'Receivable'}</ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            ×
          </CloseButton>
        </ModalHeader>
        {flashMessage && <FlashMessage message={flashMessage} />}

        {/* Balance summary */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            padding: '12px 0',
            borderBottom: '1px solid #555',
            marginBottom: 12,
          }}
        >
          <div>
            <div style={{ color: '#a0a0a0', fontSize: '0.8rem' }}>Total Owed</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {formatMoney(item?.total_owed)}
            </div>
          </div>
          <div>
            <div style={{ color: '#a0a0a0', fontSize: '0.8rem' }}>Total Paid</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {formatMoney(item?.total_paid)}
            </div>
          </div>
          <div>
            <div style={{ color: '#a0a0a0', fontSize: '0.8rem' }}>Balance</div>
            <div>
              <StatusBadgeBills $color={balanceColor(item?.balance, item?.is_settled)}>
                {formatMoney(item?.balance)}
              </StatusBadgeBills>
            </div>
          </div>
          {isFixed && (
            <div>
              <div style={{ color: '#a0a0a0', fontSize: '0.8rem' }}>Installment Amount</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                {formatMoney(item?.installment_amount)}
                {item?.number_of_installments ? (
                  <span style={{ color: '#888', fontSize: '0.75rem', marginLeft: 6 }}>
                    (×{item.number_of_installments})
                  </span>
                ) : null}
              </div>
            </div>
          )}
          {item?.installments_paid != null && (
            <div>
              <div style={{ color: '#a0a0a0', fontSize: '0.8rem' }}>Installments Paid</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                {item.installments_paid}
                {item?.number_of_installments ? (
                  <span style={{ color: '#888', fontSize: '0.75rem', marginLeft: 6 }}>
                    of {item.number_of_installments}
                  </span>
                ) : null}
              </div>
            </div>
          )}
          <div>
            <div style={{ color: '#a0a0a0', fontSize: '0.8rem' }}>Status</div>
            <div>
              <StatusBadgeBills $color={statusColor(item?.status)}>
                {item?.status || 'Ongoing'}
              </StatusBadgeBills>
            </div>
          </div>
        </div>

        {loading && <p>Loading...</p>}

        {/* Add payment received */}
        <form onSubmit={handleAddPayment}>
          <h4 style={{ marginBottom: 12 }}>Record payment received</h4>
          <FormRow>
            <Label>Date</Label>
            <BillDateInput
              name="date"
              value={newPayment.date}
              onChange={(e) => setNewPayment((p) => ({ ...p, date: e.target.value }))}
              required
            />
          </FormRow>
          <FormRow>
            <Label>Amount</Label>
            <BillNumberInput
              name="amount"
              min="0"
              value={newPayment.amount}
              onChange={(e) => setNewPayment((p) => ({ ...p, amount: e.target.value }))}
              required
            />
          </FormRow>
          <FormRow>
            <Label>Method (optional)</Label>
            <input
              name="method"
              value={newPayment.method}
              onChange={(e) => setNewPayment((p) => ({ ...p, method: e.target.value }))}
              placeholder="e.g. Cash, GCash, Bank"
              style={{
                width: '100%',
                maxWidth: 400,
                padding: '10px 12px',
                borderRadius: 6,
                border: '1px solid #555',
                backgroundColor: '#2e2e2e',
                color: '#e0e0e0',
              }}
            />
          </FormRow>
          <FormRow>
            <Label>Payment Type</Label>
            <select
              name="payment_type"
              value={newPayment.payment_type}
              onChange={(e) => setNewPayment((p) => ({ ...p, payment_type: e.target.value }))}
              style={{
                width: '100%',
                maxWidth: 400,
                padding: '10px 12px',
                borderRadius: 6,
                border: '1px solid #555',
                backgroundColor: '#2e2e2e',
                color: '#e0e0e0',
              }}
            >
              <option value="Principal">Principal</option>
              <option value="Charge">Charge</option>
            </select>
          </FormRow>
          <SubmitButton type="submit">Record Payment</SubmitButton>
        </form>

        <h4 style={{ marginTop: 24, marginBottom: 8 }}>Payments received</h4>
        <BillsTableWrapper style={{ marginTop: 8 }}>
          <BillsTableEl style={{ minWidth: 480 }}>
            <thead>
              <tr>
                <BillsTableHeader
                  onClick={toggleSortDir}
                  title="Sort by date"
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Date {sortDir === 'desc' ? '▼' : '▲'}
                </BillsTableHeader>
                <BillsTableHeader>Amount</BillsTableHeader>
                <BillsTableHeader>Type</BillsTableHeader>
                <BillsTableHeader>Method</BillsTableHeader>
                <BillsTableHeader>Actions</BillsTableHeader>
              </tr>
            </thead>
            <tbody>
              {sortedPayments.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: 16, color: '#888' }}>
                    No payments received yet. Add one above.
                  </td>
                </tr>
              ) : (
                sortedPayments.map((p) => (
                  <BillsTableRow key={p.id || p.date + p.amount}>
                    <BillsTableData>{p.date}</BillsTableData>
                    <BillsTableData>{formatMoney(p.amount)}</BillsTableData>
                    <BillsTableData>{p.payment_type || 'Principal'}</BillsTableData>
                    <BillsTableData>{p.method || '—'}</BillsTableData>
                    <BillsTableData>
                      <button
                        type="button"
                        onClick={() => handleDeletePayment(p.id)}
                        style={deleteBtnStyle}
                      >
                        Delete
                      </button>
                    </BillsTableData>
                  </BillsTableRow>
                ))
              )}
            </tbody>
          </BillsTableEl>
        </BillsTableWrapper>

        <button
          type="button"
          onClick={onClose}
          style={{
            marginTop: 16,
            padding: '8px 16px',
            background: '#555',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </ModalContent>
    </ModalBackdrop>
  );
};

export default ReceivableDetailModal;
